import { BoxTruckNearbyDriversSort, type NearbyBoxTruckDriver } from '@numeo/types-core'

type OrderableDriver = Pick<NearbyBoxTruckDriver, 'driverId' | 'proximityDistanceMi' | 'lastSeenAt'>

const compareNumbers = (a: number, b: number): number => (a === b ? 0 : a < b ? -1 : 1)

/** Missing or unreadable reads as the oldest possible update, so it sorts last. */
const seenAtMs = (driver: OrderableDriver): number => {
    const ms = driver.lastSeenAt ? Date.parse(driver.lastSeenAt) : NaN
    return Number.isNaN(ms) ? -Infinity : ms
}

const compareBySort = (a: OrderableDriver, b: OrderableDriver, sort: BoxTruckNearbyDriversSort): number =>
    sort === BoxTruckNearbyDriversSort.RECENT
        ? compareNumbers(seenAtMs(b), seenAtMs(a))
        : compareNumbers(a.proximityDistanceMi ?? Infinity, b.proximityDistanceMi ?? Infinity)

/**
 * The Nearby Drivers order: this load's bidders, then the operator's picked drivers, then everyone else —
 * each group in the chosen sort.
 *
 * The server already pages the radius in that sort, so for the rest this only places the rows it did not
 * order: bidders forced in from outside the page, pinned rows built on the client, and a row the location
 * editor just moved. The sort is stable, so rows the comparison cannot tell apart (distances rounded to
 * 0.1 mi) keep the server's exact order.
 */
export const orderNearbyDrivers = <T extends OrderableDriver>(
    drivers: T[],
    sort: BoxTruckNearbyDriversSort,
    bidderIds: ReadonlySet<string>,
    pinnedIds: ReadonlySet<string>
): T[] => {
    const group = (driver: T) => (bidderIds.has(driver.driverId) ? 0 : pinnedIds.has(driver.driverId) ? 1 : 2)
    return [...drivers].sort((a, b) => group(a) - group(b) || compareBySort(a, b, sort))
}
